// @ts-strict-ignore
import type { TextAlignType } from "../types/commonV1";
import get from "lodash/get";
import type {
  TextSizeProps,
  TextSizeV1Props
} from "../components/parts/Typography";
import type { ContentfulPageMeta } from "types/contentful";
import { generateAlternateLinks } from "./metadata";

const alignments: Array<string> = [
  "center",
  "left",
  "right",
  "justify",
  "flex-end",
  "flex-start"
];
const textHeadingSizes: Array<string> = [
  "xl",
  "lg",
  "lgCustom",
  "smCustom",
  "mdCustom",
  "xsCustom",
  "md",
  "lgV1",
  "mdV1",
  "smV1",
  "sm",
  "xs",
  "xxsV1",
  "xxsCustom",
  "xxs",
  "mdSubHeading",
  "xxsSubtitle"
];

const textBodySizes: Array<string> = [
  "xl",
  "lg",
  "lgCustom",
  "smCustom",
  "mdCustom",
  "xsCustom",
  "md",
  "lgV1",
  "mdV1",
  "smV1",
  "sm",
  "xs",
  "xxsV1",
  "xxsCustom",
  "xxs",
  "xlV1",
  "xlCustom"
];

const textDisplaySizes: Array<string> = [
  "xl",
  "lg",
  "xlV1",
  "xxl",
  "xxlCustom",
  "lgCustom",
  "mdCustom",
  "lgV1",
  "md",
  "mdV1",
  "sm",
  "xxsCustom",
  "smV1",
  "smCustom",
  "xsCustom",
  "mdSubHeading",
  "xsV1",
  "xxsV1",
  "xs",
  "mdCardSubHeading",
  "xxsHeading"
];

const textBodyV1Sizes: Array<string> = ["xs", "sm", "md", "lg", "xl"];

const typographyList: Array<string> = ["h1", "h2", "h3", "h4", "h5", "h6", "p"];

export const getAlignment = (value: string) => {
  if (value && alignments.includes(value)) return value as TextAlignType;
  return "left" as TextAlignType;
};

export const getHeadingTextSize = (value: string) => {
  if (value && textHeadingSizes.includes(value))
    return value as TextSizeProps["size"];
  return "mdV1" as TextSizeProps["size"];
};

export const getDisplaySizes = (value: string) => {
  if (value && textDisplaySizes.includes(value))
    return value as TextSizeProps["size"];
  return "lgV1" as TextSizeProps["size"];
};

export const getBodyV1Sizes = (value: string) => {
  if (value && textBodyV1Sizes.includes(value))
    return value as TextSizeV1Props["size"];
  return "md" as TextSizeV1Props["size"];
};

export const getDisplayV1Sizes = (value: string) => {
  if (value && textDisplaySizes.includes(value))
    return value as TextSizeProps["size"];
  return "mdV1" as TextSizeProps["size"];
};

export const getHeadingV1TextSize = (value: string) => {
  if (value && textHeadingSizes.includes(value))
    return value as TextSizeProps["size"];
  return "lgV1" as TextSizeProps["size"];
};

export const getHBodyTextSize = (value: string) => {
  if (value && textBodySizes.includes(value))
    return value as TextSizeProps["size"];
  return "smV1" as TextSizeProps["size"];
};

export const buildContentfulMetaData = (
  data: ContentfulPageMeta,
  slug?: string
) => {
  const metaImage = get(data, "metaImage", null);
  const url = get(data, "url", "");

  // Extract slug from URL if not provided
  let alternateSlug = slug;
  if (!alternateSlug && url) {
    try {
      const urlObj = new URL(url);
      alternateSlug = urlObj.pathname;
    } catch {
      alternateSlug = url.startsWith("/") ? url : `/${url}`;
    }
  }

  const metaData = {
    title: get(data, "metaTitle", ""),
    description: get(data, "metaDescription", ""),
    alternates: {
      canonical: url,
      languages: alternateSlug
        ? generateAlternateLinks({ slug: alternateSlug as `/${string}` })
        : {}
    },
    openGraph: {
      title: get(data, "metaTitle", ""),
      description: get(data, "metaDescription", ""),
      url: url
    },
    keywords: get(data, "keywords", []),
    image: metaImage ? `https:${metaImage?.fields?.file?.url}` : null,
    schema: get(data, "schema", {})
  };

  return metaData;
};

export const getTypography = (value: string) => {
  if (value && typographyList.includes(value)) return value;
  return undefined;
};

export interface DevicesObject<T = string> {
  mobile: T;
  tablet: T;
  desktop: T;
}

export type TextAlignOption =
  | "left"
  | "center"
  | "right"
  | "justify"
  | "initial"
  | "inherit";
export type AsOption = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
export type SizeOption = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
export type FontWeightOption =
  | "normal"
  | "bold"
  | "bolder"
  | "lighter"
  | "100"
  | "200"
  | "300"
  | "400"
  | "500"
  | "600"
  | "700"
  | "800"
  | "900"
  | "initial"
  | "inherit"
  | "revert"
  | "unset"
  | "revert-layer";
export type DisplayOption =
  | "block"
  | "inline"
  | "inline-block"
  | "flex"
  | "inline-flex"
  | "grid"
  | "inline-grid"
  | "flow-root"
  | "none"
  | "contents";

export const handleClassNamesOnScroll = ({
  className,
  hasReturn
}: {
  className: string;
  hasReturn?: boolean;
}) => {
  const onScroll = () => {
    // 686 is the height of the footer, remove when the user reaches the footer
    if (
      window.innerHeight + window.scrollY >
      document.body.clientHeight - 686
    ) {
      document.body.classList.remove(className);
    } else if (window.scrollY > 500) {
      document.body.classList.add(className);
    } else {
      document.body.classList.remove(className);
    }
  };
  window.addEventListener("scroll", onScroll);
  if (hasReturn) {
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }
};

export const handleScrollTo = (id: string) => {
  const announcementsListSection = document.getElementById(id);
  if (announcementsListSection) {
    const top =
      announcementsListSection.getBoundingClientRect().top +
      window.scrollY -
      80;
    window.scrollTo({ top: top, behavior: "smooth" });
  }
};

export const isNonEmptyArray = (arr: any): boolean => {
  return Boolean(Array.isArray(arr) && arr.length > 0);
};
